/**
 * 5 Whys renderer (artifact_name: `five_whys`).
 * JSON: { source_context, whys: WhyStep[5], conclusion }.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CardShell } from './shared/components';
import {
  parseSourceContext,
  sourceContextToHtml,
  sourceContextToJson,
  sourceContextToMarkdown,
  SourceContextCard,
  type SourceContextModel,
} from './shared/sourceContext';
import { esc, htmlDoc } from './shared/html';
import type { ArtifactRenderer } from './types';

interface WhyStep {
  question: string;
  answer: string;
}
export interface FiveWhysModel {
  source: SourceContextModel;
  whys: WhyStep[];
  conclusion: string;
}

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object';
const str = (v: unknown): string => (typeof v === 'string' ? v : '');

// -- parse / serialize -------------------------------------------------------

export function parseFiveWhys(json: Record<string, unknown>): FiveWhysModel | null {
  if (!Array.isArray(json.whys)) return null;
  const whys = json.whys
    .filter(isRecord)
    .map((w) => ({ question: str(w.question), answer: str(w.answer) }));
  if (whys.length === 0) return null;
  return {
    source: parseSourceContext(json.source_context),
    whys,
    conclusion: str(json.conclusion),
  };
}

export function fiveWhysToJson(model: FiveWhysModel): Record<string, unknown> {
  return {
    source_context: sourceContextToJson(model.source),
    whys: model.whys.map((w, i) => ({
      why_number: i + 1,
      question: w.question,
      answer: w.answer,
    })),
    conclusion: model.conclusion,
  };
}

export function fiveWhysToMarkdown(model: FiveWhysModel): string {
  const parts = [sourceContextToMarkdown(model.source), '## 5 Whys'];
  model.whys.forEach((w, i) => {
    parts.push(`**Why ${i + 1}:** ${w.question}${w.answer ? `\n${w.answer}` : ''}`);
  });
  if (model.conclusion) parts.push(`**Root cause:** ${model.conclusion}`);
  return parts.join('\n\n');
}

// -- card --------------------------------------------------------------------

const FiveWhysCard: React.FC<{ model: FiveWhysModel }> = ({ model }) => {
  const { t } = useTranslation();
  return (
  <CardShell testId="five-whys-artifact-card">
    <SourceContextCard model={model.source} />
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700 sm:p-5 bg-surface">
      <h3 className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {t('artifacts.fiveWhys.title')}
      </h3>
      <ol className="space-y-4">
        {model.whys.map((w, i) => (
          <li key={i}>
            <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
              {t('artifacts.fiveWhys.why', { n: i + 1 })}
            </div>
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {w.question}
            </div>
            {w.answer && (
              <p className="mt-0.5 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                {w.answer}
              </p>
            )}
          </li>
        ))}
      </ol>
    </div>
    {model.conclusion && (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 dark:border-emerald-800/60 dark:bg-emerald-900/20 sm:p-5">
        <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
          {t('artifacts.fiveWhys.rootCause')}
        </h3>
        <p className="text-sm leading-relaxed text-gray-800 dark:text-gray-100">
          {model.conclusion}
        </p>
      </div>
    )}
  </CardShell>
  );
};

// -- editor ------------------------------------------------------------------

const inputCls =
  'w-full resize-y rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100';

/** 5 Whys is exactly five steps — pad/truncate the incoming model to 5. */
const normalizeToFive = (whys: WhyStep[]): WhyStep[] => {
  const out = whys.slice(0, 5).map((w) => ({ ...w }));
  while (out.length < 5) out.push({ question: '', answer: '' });
  return out;
};

const FiveWhysEditor: React.FC<{
  model: FiveWhysModel;
  onReady: (getJson: () => Record<string, unknown>) => void;
}> = ({ model, onReady }) => {
  const { t } = useTranslation();
  // The schema fixes the chain at 5 — no add/remove; just five editable slots.
  const [state, setState] = React.useState<FiveWhysModel>(() => ({
    ...model,
    whys: normalizeToFive(model.whys),
  }));
  const stateRef = React.useRef(state);
  stateRef.current = state;

  React.useEffect(() => {
    onReady(() => fiveWhysToJson(stateRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mutate = (fn: (d: FiveWhysModel) => FiveWhysModel) =>
    setState((p) => fn(structuredClone(p)));

  return (
    <CardShell>
      <div className="flex flex-col gap-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700 sm:p-5">
        {state.whys.map((w, i) => (
          <div key={i} className="rounded-md border border-gray-100 p-3 dark:border-gray-800">
            <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">
              {t('artifacts.fiveWhys.why', { n: i + 1 })}
            </div>
            <input
              value={w.question}
              placeholder={t('artifacts.fiveWhys.whyPlaceholder')}
              onChange={(e) =>
                mutate((d) => {
                  d.whys[i].question = e.target.value;
                  return d;
                })
              }
              className={`${inputCls} mb-1`}
            />
            <textarea
              value={w.answer}
              rows={2}
              placeholder={t('artifacts.fiveWhys.answerPlaceholder')}
              onChange={(e) =>
                mutate((d) => {
                  d.whys[i].answer = e.target.value;
                  return d;
                })
              }
              className={inputCls}
            />
          </div>
        ))}
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-emerald-600">
            {t('artifacts.fiveWhys.rootCause')}
          </label>
          <textarea
            value={state.conclusion}
            rows={2}
            onChange={(e) => mutate((d) => ({ ...d, conclusion: e.target.value }))}
            className={inputCls}
          />
        </div>
      </div>
    </CardShell>
  );
};

// -- html --------------------------------------------------------------------

export function fiveWhysToHtml(model: FiveWhysModel, title = '5 Whys'): string {
  const steps = model.whys
    .map(
      (w, i) =>
        `<li><div class="step-label">Why ${i + 1}</div><div class="q">${esc(w.question)}</div>${w.answer ? `<p class="a">${esc(w.answer)}</p>` : ''}</li>`
    )
    .join('');
  const body = `${sourceContextToHtml(model.source)}
    <section class="card"><h3>5 Whys</h3><ol>${steps}</ol></section>
    ${model.conclusion ? `<section class="card accent"><h3>Root cause</h3><p>${esc(model.conclusion)}</p></section>` : ''}`;
  return htmlDoc(title, body);
}

export const fiveWhysRenderer: ArtifactRenderer<FiveWhysModel> = {
  type: 'five_whys',
  matchNames: ['five_whys'],
  parse: parseFiveWhys,
  toJson: fiveWhysToJson,
  toMarkdown: fiveWhysToMarkdown,
  toHtml: fiveWhysToHtml,
  Card: FiveWhysCard,
  Editor: FiveWhysEditor,
};
